import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { trackSessionStart, trackThemeToggle } from "./utils/analytics";

function App() {
  const { sessionId } = useParams();
  const [info, setInfo] = useState(null);
  const [table, setTable] = useState([]);
  const [error, setError] = useState(false);
  // cellStates: true = content visible; false = hidden (but always rendered to preserve layout)
  const [cellStates, setCellStates] = useState([]);
  // cellResults: stores null, true, or false based on the answer given for that cell
  const [cellResults, setCellResults] = useState([]);
  // cellStudied: initially false; set to true when a cell is revealed (study mode active for that cell)
  const [cellStudied, setCellStudied] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved
      ? JSON.parse(saved)
      : window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const [showQRPopup, setShowQRPopup] = useState(false); // For QR implementation if needed

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    if (sessionId) {
      fetch("https://tables-api.widenex.com/table/" + sessionId, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Session expired");
          return res.json();
        })
        .then((data) => {
          // Process markdown: extract only lines starting with "|" and split into cells.
          // Also, filter out the separator row (e.g. the second row with dashes).
          const rawRows = data.markdown
            .split("\n")
            .filter((line) => line.trim().startsWith("|"))
            .map((line) =>
              line
                .split("|")
                .slice(1, -1)
                .map((cell) => cell.trim())
            );
          const tableData = rawRows.filter((row, index) => {
            // If it's the second row and all cells are dashes (or include ":" for alignment), skip it
            if (index === 1 && row.every((cell) => /^:?-+:?$/.test(cell))) {
              return false;
            }
            return true;
          });

          if (tableData && Array.isArray(tableData) && tableData.length > 0) {
            setTable(tableData);
            const rows = tableData.length;
            const cols = tableData[0].length;
            setCellStates(
              Array.from({ length: rows }, () => Array(cols).fill(true))
            );
            setCellResults(
              Array.from({ length: rows }, () => Array(cols).fill(null))
            );
            setCellStudied(
              Array.from({ length: rows }, () => Array(cols).fill(false))
            );
          }
          setInfo({
            title: data.title,
            tags: data.tags,
            language: data.language,
          });
          trackSessionStart(sessionId);
        })
        .catch((error) => {
          console.error("Error fetching table", error);
          setError(true);
        });
    }
  }, [sessionId]);

  function toggleTheme() {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    trackThemeToggle(newTheme ? "dark" : "light");
  }

  // These functions hide or show cells without altering layout (the cell is always rendered)
  function hideAllCells() {
    setCellStates((prev) =>
      prev.map((row, i) =>
        row.map((cell, j) => (i === 0 || j === 0 ? true : false))
      )
    );
  }

  function showAllCells() {
    setCellStates((prev) => prev.map((row) => row.map(() => true)));
  }

  function hideIncorrectCells() {
    setCellStates((prev) =>
      prev.map((row, i) =>
        row.map((cell, j) =>
          i === 0 || j === 0 || cellResults[i][j] === true ? true : false
        )
      )
    );
  }

  // When clicking a cell (non-header), toggle its visibility.
  // If revealing a cell, mark it as studied.
  // If hiding a cell that was answered, reset its answer state.
  function toggleCell(row, col) {
    if (row === 0 || col === 0) return;
    setCellStates((prevStates) => {
      const newStates = prevStates.map((r, i) =>
        r.map((cell, j) => {
          if (i === row && j === col) {
            const newValue = !cell;
            if (!newValue) {
              // If hiding the cell, reset its answer state and studied flag
              setCellResults((prevResults) =>
                prevResults.map((r2, i2) =>
                  r2.map((res, j2) => (i2 === row && j2 === col ? null : res))
                )
              );
              setCellStudied((prevStudied) =>
                prevStudied.map((r2, i2) =>
                  r2.map((stud, j2) =>
                    i2 === row && j2 === col ? false : stud
                  )
                )
              );
            } else {
              // If revealing the cell, mark it as studied so the answer buttons will show
              setCellStudied((prevStudied) =>
                prevStudied.map((r2, i2) =>
                  r2.map((stud, j2) => (i2 === row && j2 === col ? true : stud))
                )
              );
            }
            return newValue;
          }
          return cell;
        })
      );
      return newStates;
    });
  }

  // Mark a cell as correct (true) or incorrect (false) and hide the answer buttons.
  function markCell(row, col, isCorrect) {
    if (row === 0 || col === 0) return;
    setCellResults((prev) =>
      prev.map((r, i) =>
        r.map((cell, j) => (i === row && j === col ? isCorrect : cell))
      )
    );
  }

  // Calculate counts for the indicator (excluding header cells)
  let hiddenCount = 0,
    learnedCount = 0,
    failedCount = 0;
  if (
    table &&
    table.length > 0 &&
    cellStates.length > 0 &&
    cellResults.length > 0
  ) {
    for (let i = 1; i < table.length; i++) {
      for (let j = 1; j < table[i].length; j++) {
        if (!cellStates[i][j]) hiddenCount++;
        if (cellResults[i][j] === true) learnedCount++;
        if (cellResults[i][j] === false) failedCount++;
      }
    }
  }

  if (!table || table.length === 0) {
    if (error || !sessionId) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
          <div className="max-w-md w-full p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md text-center">
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-2">
              Session Expired
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              The session for this table has expired or is invalid.
            </p>
          </div>
        </div>
      );
    }
    return <div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Navbar */}
      <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
        <a
          href="https://chatgpt.com/g/g-uefFoRnpX"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 
            bg-gradient-to-r from-teal-500/10 to-teal-600/10 dark:from-teal-500/20 dark:to-teal-400/20
            hover:from-teal-500/20 hover:to-teal-600/20 dark:hover:from-teal-500/30 dark:hover:to-teal-400/30
            border border-teal-200/30 dark:border-teal-400/20
            rounded-full shadow-sm backdrop-blur-sm
            transform hover:scale-105 transition-all duration-300
            print:hidden"
        >
          <img src="/logo.png" alt="Logo" className="w-6 h-6 rounded-full" />
          <span className="text-gray-700 dark:text-gray-200 font-medium">
            Summarizer GPT
          </span>
        </a>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 
            hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors print:hidden"
          aria-label="Toggle theme"
        >
          {darkMode ? (
            <svg
              className="w-6 h-6 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          ) : (
            <svg
              className="w-6 h-6 text-gray-700 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          )}
        </button>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Modern Progress Indicators */}
        <div className="mb-8 flex flex-row gap-6 justify-center items-center">
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-32 min-w-[6rem]">
            <div className="w-3 h-3 bg-gray-400 rounded-full animate-pulse"></div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Hidden
              </span>
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                {hiddenCount}/{table.length - 1}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-32 min-w-[6rem]">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Learned
              </span>
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                {learnedCount}/{table.length - 1}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-32 min-w-[6rem]">
            <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Failed
              </span>
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                {failedCount}/{table.length - 1}
              </span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <h1 className="text-3xl mb-5 mt-10 font-bold text-gray-800 dark:text-gray-100">
            {info?.title || "Study Table"}
          </h1>
          <div className="md:self-end self-start flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
            <div className="flex gap-4">
              <button
                onClick={hideAllCells}
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition duration-200"
              >
                Hide All
              </button>
              <button
                onClick={hideIncorrectCells}
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition duration-200"
              >
                Hide Incorrect
              </button>
              <button
                onClick={showAllCells}
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition duration-200"
              >
                Show All
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto shadow rounded-xl bg-white dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <tbody>
              {table.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  {row.map((cell, colIndex) => {
                    const isHeader = rowIndex === 0 || colIndex === 0;
                    const cellVisible = cellStates[rowIndex]?.[colIndex];
                    // Determine cell background color based on the answer given
                    const cellAnswerClass =
                      cellResults[rowIndex][colIndex] === true
                        ? "bg-green-200 dark:bg-green-700/50"
                        : cellResults[rowIndex][colIndex] === false
                        ? "bg-red-200 dark:bg-red-700/50"
                        : "";
                    return (
                      <td
                        key={`${rowIndex}-${colIndex}`}
                        className={`border border-gray-200 dark:border-gray-700 p-4 ${
                          isHeader
                            ? "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 font-semibold [&_strong]:font-semibold"
                            : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 cursor-pointer"
                        } relative`}
                        onClick={() => toggleCell(rowIndex, colIndex)}
                      >
                        <div
                          className={`cell-content p-2 rounded transition-colors duration-500 ${cellAnswerClass}`}
                        >
                          {/* Always render the content, but adjust opacity based on visibility */}
                          <div
                            className="cell-inner"
                            style={{
                              opacity: cellVisible ? 1 : 0,
                              transition: "opacity 0.3s",
                            }}
                          >
                            <ReactMarkdown className="prose dark:prose-dark prose-sm m-0">
                              {cell}
                            </ReactMarkdown>
                          </div>
                        </div>
                        {/* If the cell is hidden, show a placeholder with a "Tap to reveal" hint */}
                        {!cellVisible && (
                          <div className="absolute inset-0 flex items-center justify-center p-2 rounded bg-gray-200 dark:bg-gray-600">
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                              Tap to reveal
                            </span>
                          </div>
                        )}
                        {/* Answer buttons appear only on non-header cells that are visible,
                            in study mode, and not yet answered */}
                        {!isHeader &&
                          cellVisible &&
                          cellStudied[rowIndex]?.[colIndex] &&
                          cellResults[rowIndex][colIndex] === null && (
                            <div className="absolute top-1 right-1 flex gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markCell(rowIndex, colIndex, true);
                                }}
                                className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-green-500 hover:text-white transition duration-150"
                              >
                                ✓
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markCell(rowIndex, colIndex, false);
                                }}
                                className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-red-500 hover:text-white transition duration-150"
                              >
                                ✗
                              </button>
                            </div>
                          )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

export default App;
